import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtTRY, fmtDate } from "../../lib/format";

const tl = fmtTRY;
import { Wallet, Landmark, CreditCard, PiggyBank, TrendingUp, TrendingDown, ArrowDownLeft, ArrowUpRight, CalendarClock, FileText, Users, Banknote, ReceiptText, NotebookPen, ArrowRight } from "lucide-react";
import AdminBank from "./AdminBank";
import AdminCards from "./AdminCards";
import AdminKasa from "./AdminKasa";
import AdminPlans from "./AdminPlans";
import AdminChecks from "./AdminChecks";
import AdminCashflow from "./AdminCashflow";

const KIND_META = {
  tahsilat: { label: "Tahsilat", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: ArrowDownLeft },
  odeme: { label: "Ödeme", cls: "bg-rose-50 text-rose-800 border-rose-200", Icon: ArrowUpRight },
  kredi: { label: "Kredi Taksiti", cls: "bg-rose-50 text-rose-800 border-rose-200", Icon: ArrowUpRight },
  cek_alinan: { label: "Çek/Senet (Alınan)", cls: "bg-indigo-50 text-indigo-800 border-indigo-200", Icon: ArrowDownLeft },
  cek_verilen: { label: "Çek/Senet (Verilen)", cls: "bg-indigo-50 text-indigo-800 border-indigo-200", Icon: ArrowUpRight },
};

const TABS = [
  { id: "ozet", label: "Özet", Icon: Wallet },
  { id: "banka", label: "Banka & POS", Icon: Landmark, C: AdminBank },
  { id: "kartlar", label: "Firma Kredi Kartları", Icon: CreditCard, C: AdminCards },
  { id: "kasa", label: "Kasa Defteri", Icon: NotebookPen, C: AdminKasa },
  { id: "planlar", label: "Ödeme Planları", Icon: CalendarClock, C: AdminPlans },
  { id: "cek-senet", label: "Çek & Senet", Icon: FileText, C: AdminChecks },
  { id: "gelir-gider", label: "Gelir / Gider", Icon: ReceiptText, C: AdminCashflow },
];

function CashChart({ series }) {
  const W = 640, H = 200, P = 30;
  const max = Math.max(1, ...series.flatMap((s) => [s.gelir, s.gider]));
  const x = (i) => P + (i * (W - P * 2)) / Math.max(1, series.length - 1);
  const y = (v) => H - P - (v / max) * (H - P * 2);
  const path = (key) => series.map((s, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(s[key]).toFixed(1)}`).join(" ");
  return (
    <div data-testid="finance-cash-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        {[0.25, 0.5, 0.75].map((f) => <line key={f} x1={P} x2={W - P} y1={P + f * (H - P * 2)} y2={P + f * (H - P * 2)} stroke="#EDE8DE" strokeWidth="1" />)}
        <path d={path("gelir")} fill="none" stroke="#1B4332" strokeWidth="2.5" strokeLinecap="round" />
        <path d={path("gider")} fill="none" stroke="#9E472A" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="1 0" />
        {series.map((s, i) => (<g key={s.month}>
          <circle cx={x(i)} cy={y(s.gelir)} r="3.5" fill="#1B4332" />
          <circle cx={x(i)} cy={y(s.gider)} r="3.5" fill="#9E472A" />
          <text x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#78716C">{s.label}</text>
        </g>))}
      </svg>
      <div className="flex gap-4 mt-1 text-xs text-stone-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-forest inline-block rounded" /> Gelir</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-terra inline-block rounded" /> Gider</span>
      </div>
    </div>
  );
}

export default function AdminFinance() {
  const [tab, setTab] = useState("ozet");
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/admin/finance/overview").then(({ data }) => setD(data)).catch(() => toast.error("Finans verileri yüklenemedi")); }, []);
  useEffect(() => { load(); }, [load]);

  const ActiveC = TABS.find((t) => t.id === tab)?.C;
  const net = d ? d.liquidity.net : 0;
  return (
    <div data-testid="admin-finance-page" className="space-y-6">
      {/* Başlık + hızlı işlemler */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-[#EDE8DE] shadow-sm">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Finans Merkezi</h1>
          <p className="text-stone-500 text-sm mt-1">Nakit akışı, hesaplar, yaklaşan ödeme ve tahsilatlar — tek ekran.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="finance-quick-expense" onClick={() => setTab("gelir-gider")} className="flex items-center gap-1.5 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-2.5 rounded-lg transition-colors"><ArrowUpRight className="w-3.5 h-3.5" /> Hızlı Gider</button>
          <button data-testid="finance-quick-income" onClick={() => setTab("gelir-gider")} className="flex items-center gap-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2.5 rounded-lg transition-colors"><ArrowDownLeft className="w-3.5 h-3.5" /> Hızlı Gelir</button>
          <button data-testid="finance-quick-plan" onClick={() => setTab("planlar")} className="flex items-center gap-1.5 text-xs font-bold bg-forest hover:bg-forest-dark text-white px-3.5 py-2.5 rounded-lg transition-colors"><CalendarClock className="w-3.5 h-3.5" /> Plan / Kredi</button>
          <button data-testid="finance-refresh" onClick={load} className="text-xs font-semibold border border-stone-300 hover:border-forest text-stone-600 px-3.5 py-2.5 rounded-lg transition-colors">Yenile</button>
        </div>
      </div>

      {/* Modül sekmeleri */}
      <div data-testid="finance-tabs" className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`finance-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${tab === t.id ? "bg-forest text-white border-forest" : "bg-white text-stone-600 border-stone-200 hover:border-forest"}`}>
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab !== "ozet" && ActiveC && <div data-testid={`finance-module-panel-${tab}`}>{tab === "banka" ? <AdminBank types={["banka", "pos"]} /> : <ActiveC />}</div>}

      {tab === "ozet" && !d && <div data-testid="finance-loading" className="p-10 text-center text-stone-400 text-sm">Finans merkezi yükleniyor...</div>}

      {tab === "ozet" && d && (<>

      {/* Likidite kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="finance-liquidity-kasa" className="bg-white rounded-xl border border-[#EDE8DE] p-5 shadow-sm">
          <p className="text-xs text-stone-500 uppercase tracking-wider flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Kasa</p>
          <p className="text-2xl font-bold text-stone-900 mt-1.5">{tl(d.liquidity.kasa)} ₺</p>
        </div>
        <div data-testid="finance-liquidity-banka" className="bg-white rounded-xl border border-[#EDE8DE] p-5 shadow-sm">
          <p className="text-xs text-stone-500 uppercase tracking-wider flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Banka & POS</p>
          <p className="text-2xl font-bold text-stone-900 mt-1.5">{tl(d.liquidity.banka)} ₺</p>
        </div>
        <div data-testid="finance-liquidity-kart" className="bg-white rounded-xl border border-[#EDE8DE] p-5 shadow-sm">
          <p className="text-xs text-stone-500 uppercase tracking-wider flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Firma Kartı Borcu</p>
          <p className="text-2xl font-bold text-purple-700 mt-1.5">-{tl(d.liquidity.kart_borc)} ₺</p>
        </div>
        <div data-testid="finance-liquidity-net" className={`rounded-xl border p-5 shadow-sm ${net >= 0 ? "bg-forest border-forest" : "bg-terra border-terra"}`}>
          <p className="text-xs text-white/70 uppercase tracking-wider flex items-center gap-1.5"><PiggyBank className="w-3.5 h-3.5" /> Net Likidite</p>
          <p className="text-2xl font-bold text-white mt-1.5">{tl(net)} ₺</p>
        </div>
      </div>

      {/* Grafik + zaman tüneli */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 xl:col-span-8 bg-white p-6 rounded-xl border border-[#EDE8DE] shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-forest">Nakit Akışı (Son 6 Ay)</h2>
            <div className="flex items-center gap-3 text-xs">
              <span data-testid="finance-month-income" className="text-emerald-700 font-semibold">+{tl(d.month.income)} ₺</span>
              <span data-testid="finance-month-expense" className="text-terra font-semibold">-{tl(d.month.expense)} ₺</span>
              <span data-testid="finance-month-net" className={`font-bold ${d.month.net >= 0 ? "text-forest" : "text-terra"}`}>Net: {tl(d.month.net)} ₺</span>
            </div>
          </div>
          <CashChart series={d.series} />
        </div>

        <div className="lg:col-span-5 xl:col-span-4 bg-white p-6 rounded-xl border border-[#EDE8DE] shadow-sm">
          <h2 className="text-lg font-semibold text-forest mb-3">Yaklaşan Ödeme & Tahsilatlar</h2>
          <div data-testid="finance-upcoming" className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
            {d.upcoming.length === 0 && <p className="text-sm text-stone-400 text-center py-8">Önümüzdeki 45 gün içinde vade yok.</p>}
            {d.upcoming.map((u, idx) => {
              const m = KIND_META[u.kind] || KIND_META.odeme;
              return (
                <div key={idx} data-testid={`finance-upcoming-${idx}`} className={`flex items-center gap-3 border rounded-xl px-3 py-2.5 ${u.overdue ? "border-red-300 bg-red-50" : "border-stone-200"}`}>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-stone-800 truncate">{u.title}</p>
                    <p className="text-[11px] text-stone-400">{fmtDate(u.due_date)}{u.overdue && <b className="text-red-600"> • gecikti</b>}</p>
                  </div>
                  <p className={`text-sm font-bold ${u.direction === "in" ? "text-emerald-700" : "text-terra"}`}>{u.direction === "in" ? "+" : "-"}{tl(u.amount)} ₺</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cari özeti + modül geçişleri */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 xl:col-span-8 bg-white p-6 rounded-xl border border-[#EDE8DE] shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-forest">Cari Özeti</h2>
            <Link to="/admin/cari" data-testid="finance-cari-link" className="text-xs font-semibold text-forest hover:underline flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div data-testid="finance-cari-alacak" className="bg-emerald-50 border border-emerald-200 rounded-xl p-4"><p className="text-xs text-emerald-700 uppercase tracking-wider">Alacak (müşterilerden)</p><p className="text-xl font-bold text-emerald-800 mt-1">{tl(d.cari.alacak)} ₺</p></div>
            <div data-testid="finance-cari-borc" className="bg-rose-50 border border-rose-200 rounded-xl p-4"><p className="text-xs text-rose-700 uppercase tracking-wider">Borç (tedarikçilere)</p><p className="text-xl font-bold text-rose-800 mt-1">{tl(d.cari.borc)} ₺</p></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300"><th className="py-2 px-3">Cari</th><th className="py-2 px-3 text-right">Bakiye</th></tr></thead>
              <tbody>
                {d.cari.top.map((c) => (
                  <tr key={c.cari_id} className="border-b border-stone-100">
                    <td className="py-2 px-3 font-medium text-stone-800">{c.name}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${c.balance < 0 ? "text-emerald-700" : "text-terra"}`}>{c.balance < 0 ? "+" : ""}{tl(Math.abs(c.balance))} ₺ <span className="text-[10px] text-stone-400 font-normal">{c.balance < 0 ? "alacak" : "borç"}</span></td>
                  </tr>
                ))}
                {d.cari.top.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-stone-400 text-sm">Bakiyeli cari yok</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-5 xl:col-span-4 space-y-6">
          <div className="bg-white p-6 rounded-xl border border-[#EDE8DE] shadow-sm">
            <h2 className="text-lg font-semibold text-forest mb-1">Finansal Raporlar</h2>
            <p className="text-xs text-stone-500 mb-3">PDF/Excel çıktıları Raporlar bölümünden alınır.</p>
            <div className="space-y-2">
              <Link to="/admin/raporlar" data-testid="finance-report-karzarar" className="flex items-center gap-2 text-sm font-semibold text-forest hover:underline"><TrendingUp className="w-4 h-4" /> Kar / Zarar Raporu</Link>
              <Link to="/admin/raporlar" data-testid="finance-report-kasa" className="flex items-center gap-2 text-sm font-semibold text-forest hover:underline"><Banknote className="w-4 h-4" /> Kasa Defteri (PDF/Excel)</Link>
              <Link to="/admin/raporlar" data-testid="finance-report-plans" className="flex items-center gap-2 text-sm font-semibold text-forest hover:underline"><CalendarClock className="w-4 h-4" /> Ödeme Planları Yaşlandırma</Link>
            </div>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
